import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  AlimCustomerDetailsResponse,
  AlphaProductFlagResponse,
  FolderContents,
  PostcodeDetailsResponse,
  RedirectUrlResponse,
  RoutingUrlResponse,
} from "src/app/shared/interfaces/customer-management";
import { HttpHelperService } from "src/app/shared/services/http-helper.service";

@Injectable({
  providedIn: "root",
})
export class CustomerManagementService {
  constructor(
    private readonly httpHelperService: HttpHelperService,
    private readonly http: HttpClient,
  ) {}

  public getCustomerManagementLov(payload) {
    let requestURL = "/customer/lov";
    if (payload.userId) {
      requestURL = requestURL.concat(`?userId=${payload.userId}`);
    }
    if (payload.branchCode) {
      requestURL = requestURL.concat(`&branchCode=${payload.branchCode}`);
    }
    return this.httpHelperService.get(requestURL);
  }

  public getCustomerManagementLovV2(payload) {
    return this.httpHelperService.post(
      `/customer/lov?userId=${payload.userId}`,
      payload.requestBody,
    );
  }

  public getEnquiryListing(payload) {
    return this.httpHelperService.post(`/customer/enquiry`, payload);
  }

  public getAdminCrossSellListing(userId: string) {
    return this.httpHelperService.get(`/admin/cross-sells?userId=${userId}`);
  }

  public archiveCrossSell(payload) {
    return this.httpHelperService.post(
      `/admin/cross-sells/${payload.crossSellId}/archive`,
      payload,
    );
  }

  public createCrossSell() {
    return this.httpHelperService.postXmlRequest(`/admin/cross-sells`);
  }

  public editCrossSell(crossSellId: string | number) {
    return this.httpHelperService.postXmlRequest(
      `/admin/cross-sells/${crossSellId}`,
    );
  }

  public getCrossSellEnquiry(payload) {
    return this.httpHelperService.get(
      `/customer/cross-sell/enquiry?agentCode=${payload.agentCode}&clientType=${payload.clientType}&crossSellId=${payload.crossSellId}`,
    );
  }

  public downloadReport(payload) {
    let header = new HttpHeaders();
    header.append("Accept", "application/json");
    let options = {
      headers: header,
      responseType: "arraybuffer",
      observe: "response",
    };
    return this.httpHelperService.postRaw(
      "/customer/report/download",
      payload,
      options,
    );
  }

  public getStructuredFolder(body): Observable<FolderContents> {
    return this.httpHelperService.post(
      `/cms/folder/structured-contents/folders-contents`,
      body,
    );
  }

  public saveEmailLog(payload) {
    return this.httpHelperService.post(
      "/customer/cross-sell/email-sent",
      payload,
    );
  }

  public saveMessageLog(payload) {
    return this.httpHelperService.post(
      "/customer/cross-sell/msg-sent/create",
      payload,
    );
  }

  public getCustomerDetails(payload) {
    return this.httpHelperService.post("/customer/search", payload);
  }

  public getCustomerPolicyList(payload) {
    return this.httpHelperService.post("/customer/policy-list", payload);
  }

  public getGwpData(payload) {
    return this.httpHelperService.post("/customer/custgwp", payload);
  }

  public getPostcodeDetails(payload) {
    return this.httpHelperService.get(
      `/customer/postcode/detail?postcode=${payload}`,
    );
  }

  public updateCustomerDetails(payload) {
    return this.httpHelperService.post("/customer/update", payload);
  }

  public getCustomerEndtHistory(payload) {
    return this.httpHelperService.get(
      `/customer/change-history?partnerId=${payload.partnerId}&userId=${payload.userId}`,
    );
  }

  public getCustomerClaimEnquiry(payload) {
    return this.httpHelperService.get(`/customer/claims?policyNo=${payload}`);
  }

  public getCustomerPolicyDetails(payload) {
    let header = new HttpHeaders();
    header.append("Accept", "application/json");
    let options = {
      headers: header,
      responseType: "arraybuffer",
      observe: "response",
    };
    return this.httpHelperService.postRaw(
      "/customer/view-policy",
      payload,
      options,
    );
  }

  public getCustomerInteractionData(payload) {
    return this.httpHelperService.post("/customer/interaction-data", payload);
  }

  public getRedirectUrl(payload): Observable<RedirectUrlResponse> {
    const {
      operator,
      agentCode,
      action,
      contractId,
      documentNo,
      productType,
      productCode,
      cnoteType,
      idType,
      idValue,
      clientType,
      policyNo,
      renewNow,
      vehNo,
    } = payload;

    const queryParams = [`userId=${operator}`];

    if (agentCode) {
      queryParams.push(`agentCode=${agentCode}`);
    }

    if (action) {
      queryParams.push(`action=${action}`);
    }

    if (documentNo) {
      queryParams.push(`documentNo=${documentNo}`);
    }

    if (policyNo) {
      queryParams.push(`policyNo=${policyNo}`);
    }

    if (productType) {
      queryParams.push(`productType=${productType}`);
    }

    if (contractId) {
      queryParams.push(`contractId=${contractId}`);
    }

    if (cnoteType) {
      queryParams.push(`cnoteType=${cnoteType}`);
    }

    if (productCode) {
      queryParams.push(`productCode=${productCode}`);
    }

    if (idType) {
      queryParams.push(`idType=${idType}`);
    }

    if (idValue) {
      queryParams.push(`idValue=${idValue}`);
    }

    if (clientType) {
      queryParams.push(`clientType=${clientType}`);
    }

    if (renewNow) {
      queryParams.push(`renewNow=${renewNow}`);
    }

    if (vehNo) {
      queryParams.push(`vehNo=${vehNo}`);
    }

    return this.httpHelperService.get(`/ud/switching?${queryParams.join("&")}`);
  }

  public getAlphaProductFlag(payload): Observable<AlphaProductFlagResponse> {
    let contractParams = "";
    let actionParams = "";

    if (payload.contractId) {
      contractParams = `&contractId=${payload.contractId}`;
    }
    if (payload.action) {
      actionParams = `&action=${payload.action}`;
    }

    return this.httpHelperService.get(
      `/ud/check-alpha-route?productCode=${payload.productCode}&userId=${payload.userId}${contractParams}${actionParams}`,
    );
  }

  public getAlphaRoutingURL(payload): Observable<RoutingUrlResponse> {
    const {
      action,
      productType,
      productCode,
      agentCode,
      idType,
      idValue,
      clientType,
      userId,
      documentNo,
    } = payload;

    let queryParamsString = `?switch=true&action=${action}`;

    if (documentNo) {
      queryParamsString = queryParamsString.concat(`&documentNo=${documentNo}`);
    }

    if (productType) {
      queryParamsString = queryParamsString.concat(
        `&productType=${productType}`,
      );
    }

    if (productCode) {
      queryParamsString = queryParamsString.concat(
        `&productCode=${productCode}`,
      );
    }

    if (agentCode) {
      queryParamsString = queryParamsString.concat(`&agentCode=${agentCode}`);
    }

    if (idType) {
      queryParamsString = queryParamsString.concat(`&idType=${idType}`);
    }

    if (idValue) {
      queryParamsString = queryParamsString.concat(`&idValue=${idValue}`);
    }

    if (clientType) {
      queryParamsString = queryParamsString.concat(`&clientType=${clientType}`);
    }

    if (userId) {
      queryParamsString = queryParamsString.concat(`&userId=${userId}`);
    }

    return this.httpHelperService.get(`/session/route${queryParamsString}`);
  }

  public getCoverNoteValidation(payload) {
    return this.httpHelperService.get(
      `/customer/coverNote/validate?cnoteNo=${payload.cnoteNo}&userId=${payload.userId}`,
    );
  }

  public getCrossSellStatePostcode(body): Observable<PostcodeDetailsResponse> {
    return this.httpHelperService.post(
      `/admin/cross-sells/validate-state-postcode`,
      body,
    );
  }

  public getPostCodeByUploadTemplate() {
    return this.httpHelperService.postXmlRequest(
      "/admin/cross-sells/postcode/upload",
    );
  }

  public getPolicyValidation(payload) {
    const { policyNo, userId, productCode, expiryDate } = payload;
    let queryParamsString = `?`;
    if (policyNo) {
      queryParamsString = queryParamsString.concat(`policyNo=${policyNo}`);
    }
    if (userId) {
      queryParamsString = queryParamsString.concat(`&userId=${userId}`);
    }
    if (productCode) {
      queryParamsString = queryParamsString.concat(
        `&productCode=${productCode}`,
      );
    }
    if (expiryDate) {
      queryParamsString = queryParamsString.concat(`&expiryDate=${expiryDate}`);
    }

    return this.httpHelperService.get(
      `/customer/policy/validate${queryParamsString}`,
    );
  }

  public getAlimCPDetails(payload): Observable<AlimCustomerDetailsResponse> {
    return this.httpHelperService.post(
      "/customer/enquiry/alim/customer",
      payload,
    );
  }
}
